import type { AdminRole, Permission } from "./api";

/**
 * Menu du back-office — palier 1 de l'audit du 18/09 (admin V1.25, accord
 * d'Olivier du 24/09). Source UNIQUE de la barre latérale, de la palette Cmd+K
 * et des raccourcis du tableau de bord : un libellé ou un droit ne se change
 * qu'ici.
 *
 * Règle : une entrée apparaît si le compte peut au moins LIRE la page côté
 * serveur ; dans la page, un bouton n'apparaît que si le serveur accepte
 * l'action. V1.51 (Gestion des droits) : chaque entrée porte le DROIT requis
 * (`perm`), et non plus une liste de rôles en dur — les droits du compte
 * viennent du serveur (`me().permissions`, cf. lib/permissions.ts), réglages
 * de la Gestion des droits compris. Sans `perm`, l'entrée est visible de tous.
 */

export interface NavItem {
  to: string;
  label: string;
  /** Si présent, l'entrée n'est visible qu'avec ce droit. */
  perm?: Permission;
  /** Mots-clés de la palette Cmd+K, en plus du libellé. */
  keywords?: string;
}

export interface NavSection {
  title: string;
  defaultOpen: boolean;
  items: NavItem[];
}

/** Épinglé en haut de la barre latérale, hors sections. */
export const DASHBOARD: NavItem = { to: "/", label: "Tableau de bord", keywords: "dashboard accueil" };

export const NAV_SECTIONS: NavSection[] = [
  {
    title: "Aide",
    defaultOpen: true,
    items: [
      { to: "/guide", label: "Guide utilisateur", keywords: "aide documentation" },
      // FAQ du back-office (seed), à ne pas confondre avec la FAQ visiteur à venir.
      { to: "/faqs", label: "Questions fréquentes", keywords: "faq aide" },
    ],
  },
  {
    title: "Contenus",
    defaultOpen: true,
    items: [
      { to: "/collections", label: "Collections permanentes", perm: "content.read", keywords: "collection" },
      { to: "/exhibitions", label: "Expositions temporaires", perm: "content.read", keywords: "expo exposition" },
      { to: "/pois", label: "Points d'intérêt", perm: "content.read", keywords: "poi oeuvres œuvres works" },
      { to: "/parcours", label: "Parcours", perm: "content.read", keywords: "tours" },
      { to: "/rooms", label: "Salles", perm: "content.read", keywords: "plan" },
      // 28/10/2026 : localisation des oeuvres (export TMS hebdomadaire) et import
      // des plans de niveau au format GeoJSON.
      { to: "/tms", label: "Salles et TMS", perm: "content.read", keywords: "tms synchronisation zones plan geojson couverture seuils" },
      { to: "/media", label: "Médias", perm: "content.read", keywords: "images audios bibliothèque" },
      // V1.28 : FAQ visiteur et infos pratiques par établissement (onglet Infos de la PWA).
      { to: "/visitor-info", label: "Infos visiteur", perm: "content.read", keywords: "faq infos pratiques horaires services visiteur" },
      // Tout l'import est en écriture (modèle CSV compris) : inutile aux autres rôles.
      { to: "/import", label: "Import", perm: "content.write", keywords: "import en bloc csv excel assistant" },
      // 28/10/2026 : import en masse, vue d'ensemble et calage par l'IA au meme
      // endroit — l'outil IA n'est plus une entree isolee.
      { to: "/slideshows", label: "Diaporamas et time codes", perm: "content.write", keywords: "timecode time code diaporama ia calage images synchronise" },
    ],
  },
  {
    title: "Parcours enfants",
    defaultOpen: true,
    items: [
      { to: "/treasure-hunts", label: "Chasses au trésor", perm: "content.read", keywords: "chasse enfants kids" },
      { to: "/mini-games", label: "Mini-jeux", perm: "content.read", keywords: "jeux quiz enfants kids" },
    ],
  },
  {
    title: "Applications",
    defaultOpen: false,
    items: [
      // Une seule entrée (V1.02) : standard / mono-parcours est un filtre de la page.
      { to: "/pwa-projects", label: "Applications PWA", perm: "content.read", keywords: "pwa projets mono" },
      { to: "/establishments", label: "Établissements", perm: "content.read", keywords: "etablissements musées" },
    ],
  },
  {
    title: "Billetterie et ventes",
    defaultOpen: false,
    items: [
      { to: "/partners", label: "Partenaires", perm: "billing.manage", keywords: "see tickets revendeurs octo diagnostic codes de test" },
      { to: "/sav/purchases", label: "Achats et accès", perm: "billing.manage", keywords: "achats manuels sav accès" },
      // V1.33 : lots de codes à usage unique imprimés en QR (Carte Blanche, Compensation).
      { to: "/counter-codes", label: "Codes comptoir", perm: "billing.manage", keywords: "carte blanche compensation qr imprimer planche lots codes comptoir" },
    ],
  },
  {
    title: "Reporting",
    defaultOpen: true,
    items: [
      { to: "/reporting/billetterie", label: "Billetterie", perm: "reporting.finance", keywords: "ventes annulations activations" },
      { to: "/reporting/redevance", label: "Redevance", perm: "reporting.finance", keywords: "redevance royautes relevé" },
      { to: "/reporting/visiteurs", label: "Visiteurs", perm: "analytics.read", keywords: "statistiques usage stats" },
      { to: "/reporting/qualite", label: "Qualité", perm: "analytics.read", keywords: "incidents bugs signalements" },
    ],
  },
  {
    title: "Administration",
    defaultOpen: false,
    items: [
      { to: "/tenants", label: "Clients", perm: "tenant.manage", keywords: "tenants" },
      { to: "/users", label: "Utilisateurs", perm: "users.manage", keywords: "users droits rôles gestion des droits permissions" },
      // 02/11/2026 : les droits d'UNE PERSONNE, par-dessus son rôle.
      {
        to: "/droits-par-personne",
        label: "Droits par personne",
        perm: "rights.manageUser",
        keywords: "droits individuels permissions personne compte surcharge exception google mcp",
      },
      { to: "/demo-access", label: "Accès démo et previews", perm: "demoAccess.manage", keywords: "demo preview" },
      { to: "/audit", label: "Journal d'audit", perm: "audit.read", keywords: "audit historique traces" },
      // 02/11/2026 : facturation propre d'ALTO, verrouillee au Super-admin.
      {
        to: "/groupes-notre-dame",
        label: "Groupes Notre-Dame",
        perm: "ndp.manage",
        keywords: "ndp ingenie dossiers facturation siret audiophones groupes",
      },
    ],
  },
];

export function canSeeNav(item: Pick<NavItem, "perm">, perms: ReadonlySet<Permission>): boolean {
  return !item.perm || perms.has(item.perm);
}

/**
 * Droit requis pour une ADRESSE (palier 2, admin V1.30) : celui de l'entrée du
 * menu correspondante ; une fiche (/pois/:id, /parcours/:id…) suit sa liste.
 * `undefined` = ouverte à tous (tableau de bord, Mon compte, pages hors menu).
 * Le serveur reste la vraie barrière : ceci évite seulement d'afficher une
 * page inutilisable à qui l'ouvre par son adresse.
 */
export function permForPath(pathname: string): Permission | undefined {
  const path = pathname.replace(/\/+$/, "") || "/";
  // Même page que /pwa-projects, filtrée sur les mono-parcours (anciens signets).
  const normalized = path.replace(/^\/pwa-projects-mono(?=\/|$)/, "/pwa-projects");
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (normalized === item.to || normalized.startsWith(item.to + "/")) return item.perm;
    }
  }
  return undefined;
}

export const ROLE_LABEL: Record<AdminRole, string> = {
  SUPER_ADMIN: "Super-admin",
  ADMIN: "Admin",
  EDITOR: "Éditeur",
  SAV: "SAV",
  VIEWER: "Lecture seule",
  COMMERCIAL: "Commercial",
};

